import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import { ClipboardList, StickyNote, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const ClientProjectTab = ({
  language,
  client,
  setClient,
  clientId,
  clientEstimates,
  newProjectNote,
  setNewProjectNote
}) => {
  const handleAddProjectNote = async () => {
    if (!newProjectNote.trim()) return;
    const note = {
      id: Date.now().toString(),
      content: newProjectNote,
      note_type: 'project',
      created_at: new Date().toISOString()
    };
    try {
      await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          project_notes: [...(client.project_notes || []), note]
        })
      });
      setClient(prev => ({ ...prev, project_notes: [...(prev.project_notes || []), note] }));
      setNewProjectNote('');
      toast.success(language === 'es' ? 'Nota agregada' : 'Note added');
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleDeleteProjectNote = async (idx) => {
    const updated = (client.project_notes || []).filter((_, i) => i !== idx);
    await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_notes: updated })
    });
    setClient(prev => ({ ...prev, project_notes: updated }));
  };

  const handleProgressChange = async (stepId, field, value) => {
    const progress = client.construction_progress || {};
    const newProgress = {
      ...progress,
      [stepId]: { 
        ...progress[stepId],
        [field]: value,
        ...(field === 'completed' && !progress[stepId]?.date ? { date: '', note: '' } : {})
      }
    };
    await fetch(`${BACKEND_URL}/api/clients/${clientId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ construction_progress: newProgress })
    });
    setClient(prev => ({ ...prev, construction_progress: newProgress }));
  };

  const progressSteps = [
    { id: 'contract_signed', label: language === 'es' ? 'Contrato Firmado' : 'Contract Signed' },
    { id: 'permit_applied', label: language === 'es' ? 'Permiso Solicitado' : 'Permit Applied For' },
    { id: 'permit_granted', label: language === 'es' ? 'Permiso Otorgado' : 'Permit Granted' },
    { id: 'hoa_applied', label: language === 'es' ? 'HOA Solicitado' : 'HOA Application Filed' },
    { id: 'hoa_granted', label: language === 'es' ? 'HOA Aprobado' : 'HOA Application Granted' },
    { id: 'materials_ordered', label: language === 'es' ? 'Materiales Pedidos' : 'Materials Ordered' },
    { id: 'materials_delivered', label: language === 'es' ? 'Materiales Entregados' : 'Materials Delivered' },
    { id: 'job_started', label: language === 'es' ? 'Trabajo Iniciado' : 'Job Started' },
    { id: 'inspection_1', label: language === 'es' ? '1ra Inspección' : '1st Inspection' },
    { id: 'inspection_2', label: language === 'es' ? '2da Inspección' : '2nd Inspection' },
    { id: 'final_inspection', label: language === 'es' ? 'Inspección Final' : 'Final Inspection' },
    { id: 'job_completed', label: language === 'es' ? 'Trabajo Completado' : 'Job Completed' },
    { id: 'payment_received', label: language === 'es' ? 'Pago Recibido' : 'Payment Received' }
  ];

  return (
    <div className="space-y-6">
      {/* Project Header Card */}
      <Card className="bg-gradient-to-r from-amber-500 to-amber-600">
        <CardHeader className="text-white">
          <CardTitle className="flex items-center">
            <ClipboardList className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Flujo de Construcción' : 'Construction Flow Chart'}
          </CardTitle>
        </CardHeader>
        <CardContent className="bg-white rounded-b-lg pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-500">{language === 'es' ? 'Número de Trabajo' : 'Job Number'}</Label>
              <Input 
                value={client.job_number || clientEstimates[0]?.proposal_id || ''} 
                className="font-mono" 
                readOnly
              />
            </div>
            <div>
              <Label className="text-gray-500">{language === 'es' ? 'Fecha del Presupuesto' : 'Estimate Date'}</Label>
              <Input 
                type="date" 
                value={client.estimate_date || ''} 
                onChange={(e) => {
                  setClient(prev => ({ ...prev, estimate_date: e.target.value }));
                }}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Project Notes */}
      <Card>
        <CardHeader className="bg-blue-600 text-white rounded-t-lg">
          <CardTitle className="flex items-center">
            <StickyNote className="mr-2 h-5 w-5" />
            {language === 'es' ? 'Notas del Proyecto' : 'Project Notes'}
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="flex gap-2">
            <Textarea 
              placeholder={language === 'es' ? 'Agregar nota del proyecto...' : 'Add project note...'}
              value={newProjectNote}
              onChange={(e) => setNewProjectNote(e.target.value)}
              className="flex-1"
            />
            <Button 
              onClick={handleAddProjectNote}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          {(client.project_notes || []).length > 0 && (
            <div className="mt-4 space-y-2">
              {client.project_notes.map((note, idx) => (
                <div key={idx} className="flex items-start gap-2 p-2 bg-blue-50 rounded">
                  <p className="flex-1 text-sm">{note.content}</p>
                  <span className="text-xs text-gray-400">
                    {new Date(note.created_at).toLocaleDateString()}
                  </span>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-500 h-6 w-6 p-0"
                    onClick={() => handleDeleteProjectNote(idx)}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Project Progress */}
      <Card>
        <CardHeader>
          <CardTitle>{language === 'es' ? 'Progreso del Proyecto' : 'Project Progress'}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {progressSteps.map((step, idx) => {
              const progress = client.construction_progress || {};
              const isComplete = progress[step.id]?.completed;
              return (
                <div key={step.id} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-gray-50">
                  <span className="text-gray-500 w-6">{idx + 1}.</span>
                  <input 
                    type="checkbox" 
                    checked={isComplete || false}
                    onChange={(e) => handleProgressChange(step.id, 'completed', e.target.checked)}
                    className="h-5 w-5 rounded border-gray-300"
                  />
                  <span className={`flex-1 ${isComplete ? 'line-through text-gray-400' : ''}`}>
                    {step.label}
                  </span>
                  <Input 
                    type="date" 
                    value={progress[step.id]?.date || ''} 
                    onChange={(e) => handleProgressChange(step.id, 'date', e.target.value)}
                    className="w-40"
                  />
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Project Stage Legend */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-4 justify-center">
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-orange-500 rounded"></span> {language === 'es' ? 'Pre-Construcción' : 'Pre-Construction'}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> {language === 'es' ? 'Materiales' : 'Materials'}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-blue-500 rounded"></span> {language === 'es' ? 'Construcción' : 'Construction'}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded"></span> {language === 'es' ? 'Inspección' : 'Inspection'}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> {language === 'es' ? 'Pago' : 'Payment'}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 bg-emerald-600 rounded"></span> {language === 'es' ? 'Cerrado' : 'Closed'}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientProjectTab;
